// Obra Radar — módulo nativo para rentalmaq (renta de maquinaria).
// Radar de demanda constructiva nacional: dónde se va a construir = dónde rentar.
// Consume el proxy gateado de Orión (/orion/api/radar/*). Tema semántico (tokens).

import { useEffect, useState, type ReactNode } from 'react';
import {
  Radar, MapPin, HardHat, Building2, Phone, Mail, ArrowLeft,
  Loader2, Sparkles, TrendingUp, AlertTriangle, FileText, Leaf, Factory,
} from 'lucide-react';

import { getEstados, getEstado, preguntar } from '../api';
import type { EstadoResumen, EstadoDetalle } from '../types';

const money = (n: number) =>
  `$${new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(Math.round(n / 1e6))} M`;
const num = (n: number) => new Intl.NumberFormat('es-MX').format(n);
// Limpieza ligera de markdown (los briefs/respuestas vienen con **bold**, #, listas):
// quitamos marcadores para lectura limpia en texto plano (pre-wrap conserva saltos).
const cleanMd = (s: string) =>
  (s || '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/^\s*[-*]\s+/gm, '• ')
    .trim();

export function ObraRadarPage() {
  const [estados, setEstados] = useState<EstadoResumen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sel, setSel] = useState<EstadoDetalle | null>(null);
  const [selLoading, setSelLoading] = useState(false);

  useEffect(() => {
    getEstados()
      .then((e) => setEstados(e.sort((a, b) => b.obras - a.obras)))
      .catch((e) => setError(String(e.message || e)))
      .finally(() => setLoading(false));
  }, []);

  const openEstado = async (nombre: string) => {
    setSelLoading(true);
    setError(null);
    try {
      setSel(await getEstado(nombre));
    } catch (e: any) {
      setError(String(e.message || e));
    } finally {
      setSelLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="animate-spin mr-2" size={18} /> Cargando radar nacional…
      </div>
    );
  }

  if (error && !sel) {
    return (
      <div className="m-6 rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        <AlertTriangle className="inline mr-2 text-red-500" size={16} />
        No se pudo cargar Obra Radar: {error}
      </div>
    );
  }

  if (sel) return <EstadoDetail data={sel} loading={selLoading} onBack={() => setSel(null)} />;

  const totalObras = estados.reduce((s, e) => s + e.obras, 0);
  const totalMias = estados.reduce((s, e) => s + (e.mias || 0), 0);
  const totalPipeMin = estados.reduce((s, e) => s + e.pipeline_min, 0);

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-start gap-3">
        <Radar className="text-primary mt-1" size={28} />
        <div>
          <h1 className="text-xl font-semibold text-foreground">Obra Radar — Demanda constructiva</h1>
          <p className="text-sm text-muted-foreground">
            Radar nacional de obra en puerta. Donde se va a construir es donde se va a rentar maquinaria.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Estados con demanda" value={num(estados.length)} icon={<MapPin size={16} />} />
        <StatCard label="Obras en radar" value={num(totalObras)} icon={<HardHat size={16} />} />
        <StatCard label="Señales MIA" value={num(totalMias)} icon={<Leaf size={16} />} />
        <StatCard label="Pipeline nacional (mín.)" value={money(totalPipeMin)} icon={<TrendingUp size={16} />} />
      </div>

      {/* Pregúntale al radar (killer feature) — arriba, siempre visible. */}
      <ConsultarIA />

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="px-4 py-2 font-medium">Estado</th>
              <th className="px-4 py-2 font-medium text-right">Obras</th>
              <th className="px-4 py-2 font-medium text-right">MIA</th>
              <th className="px-4 py-2 font-medium text-right">Zonas</th>
              <th className="px-4 py-2 font-medium text-right">Pipeline (mín.–máx.)</th>
            </tr>
          </thead>
          <tbody>
            {estados.map((e) => (
              <tr
                key={e.id}
                onClick={() => openEstado(e.estado)}
                className="cursor-pointer border-b border-border last:border-0 hover:bg-muted transition-colors"
              >
                <td className="px-4 py-2 font-medium text-foreground">{e.estado}</td>
                <td className="px-4 py-2 text-right text-foreground">{num(e.obras)}</td>
                <td className="px-4 py-2 text-right text-muted-foreground">{e.mias ? num(e.mias) : '—'}</td>
                <td className="px-4 py-2 text-right text-muted-foreground">{e.zonas}</td>
                <td className="px-4 py-2 text-right text-muted-foreground">
                  {money(e.pipeline_min)} – {money(e.pipeline_max)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string; icon: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon}{label}</div>
      <div className="mt-1 text-lg font-semibold text-foreground">{value}</div>
    </div>
  );
}

function EstadoDetail({ data, loading, onBack }: { data: EstadoDetalle; loading: boolean; onBack: () => void }) {
  const provCats = Object.entries(data.proveedores_objetivo || {}).filter(([, v]) => (v || []).length > 0);
  const cruce = data.cruce || [];
  const briefs = data.ai?.briefs || [];
  const mias = data.mias_destacadas || [];
  const contactos = data.contactos_promovente || [];
  const recurrentes = data.recurrentes || [];
  const deps = data.dependencias || [];

  return (
    <div className="p-6 space-y-6">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-primary hover:underline">
        <ArrowLeft size={15} /> Radar nacional
      </button>

      <header>
        <h1 className="text-xl font-semibold text-foreground">{data.estado}</h1>
        <div className="mt-2 flex flex-wrap gap-3 text-sm text-muted-foreground">
          <span><b className="text-foreground">{num(data.obras)}</b> obras</span>
          <span><b className="text-foreground">{num(data.mias)}</b> con MIA (impacto ambiental)</span>
          <span>Pipeline <b className="text-foreground">{money(data.pipeline_min)}–{money(data.pipeline_max)}</b></span>
        </div>
      </header>

      {loading && <div className="text-sm text-muted-foreground"><Loader2 className="inline animate-spin mr-1" size={14} />Actualizando…</div>}

      {/* Pregúntale al radar, scopeado a ESTE estado (lente de renta). */}
      <ConsultarIA estado={data.estado} />

      {/* Detonadores obra×MIA — la señal temprana, nuestro diferenciador. */}
      {cruce.length > 0 && (
        <Section title="Detonadores en puerta (obra × MIA)" icon={<Factory size={16} />}>
          <div className="space-y-2">
            {cruce.slice(0, 20).map((c, i) => {
              const detonador = c.industrial_grande || c.detonador;
              return (
                <div key={i} className={`rounded-lg border px-3 py-2 text-sm ${detonador ? 'border-primary/50 bg-primary/5' : 'border-border bg-card'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-foreground font-medium">{c.proyecto}</span>
                    {detonador && (
                      <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                        {c.industrial_grande ? 'industrial grande' : 'detonador'}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {c.promovente} · {c.municipio}{c.giro ? ` · giro ${c.giro}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Briefs comerciales generados por IA — con estimación $. Alto valor vendedor. */}
      {briefs.length > 0 && (
        <Section title="Briefs comerciales (IA)" icon={<FileText size={16} />}>
          <div className="space-y-3">
            {briefs.slice(0, 12).map((b, i) => (
              <div key={i} className="rounded-lg border border-border bg-card px-4 py-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-foreground font-medium">{b.proyecto}</span>
                  {b.estimacion && (
                    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">
                      {money(b.estimacion.inversion_min_mxn)}–{money(b.estimacion.inversion_max_mxn)}
                      <span className="text-muted-foreground"> · confianza {b.estimacion.confianza}</span>
                    </span>
                  )}
                </div>
                {b.municipio && <div className="mt-0.5 text-xs text-muted-foreground">{b.municipio}</div>}
                {b.brief && (
                  <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground leading-relaxed">
                    {cleanMd(b.brief)}
                  </p>
                )}
                {b.estimacion?.base && (
                  <p className="mt-2 text-[11px] text-muted-foreground/70">Base: {b.estimacion.base}</p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Señales ambientales (MIA destacadas) — obra ANTES de licitarse. */}
      {mias.length > 0 && (
        <Section title="Señales ambientales (MIA destacadas)" icon={<Leaf size={16} />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {mias.slice(0, 12).map((m, i) => (
              <div key={i} className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-foreground">{m.proyecto}</span>
                  {m.industrial_grande && (
                    <span className="shrink-0 rounded-full bg-primary/80 px-2 py-0.5 text-xs text-primary-foreground">industrial</span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {[m.promovente, m.municipio, m.tipo_probable, m.fecha].filter(Boolean).join(' · ')}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Zonas calientes" icon={<MapPin size={16} />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {(data.zonas || []).slice(0, 16).map((z, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-sm">
              <span className="text-foreground">{z.municipio}</span>
              <span className="text-muted-foreground">
                {num(z.obras)} obras · score {Math.round(z.score)}
                {z.industrial ? ` · ${z.industrial} ind.` : ''}
              </span>
            </div>
          ))}
        </div>
      </Section>

      {provCats.length > 0 && (
        <Section title="Proveedores objetivo (competencia / aliados)" icon={<HardHat size={16} />}>
          {provCats.map(([cat, provs]) => (
            <div key={cat} className="mb-3">
              <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">{cat}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {(provs || []).slice(0, 8).map((p, i) => (
                  <div key={i} className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
                    <div className="text-foreground">{p.nombre || '(sin nombre)'}</div>
                    <div className="mt-0.5 flex flex-wrap gap-3 text-xs text-muted-foreground">
                      {p.tel && <span className="flex items-center gap-1"><Phone size={12} />{p.tel}</span>}
                      {p.correo && <span className="flex items-center gap-1"><Mail size={12} />{p.correo}</span>}
                      {p.municipio && <span>{p.municipio}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Section>
      )}

      {contactos.length > 0 && (
        <Section title="Contactos de promovente" icon={<Phone size={16} />}>
          <div className="space-y-2">
            {contactos.slice(0, 12).map((c, i) => (
              <div key={i} className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
                <div className="text-foreground">{c.promovente}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{c.proyecto} · giro {c.giro}{c.contacto ? ` · ${c.contacto}` : ''}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(recurrentes.length > 0 || deps.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {recurrentes.length > 0 && (
            <Section title="Promoventes recurrentes (cuentas clave)" icon={<TrendingUp size={16} />}>
              <ul className="space-y-1 text-sm">
                {recurrentes.slice(0, 10).map((r, i) => (
                  <li key={i} className="flex justify-between text-muted-foreground">
                    <span className="text-foreground">{r.promovente}</span><span>{r.n}×</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {deps.length > 0 && (
            <Section title="Dependencias activas" icon={<Building2 size={16} />}>
              <div className="flex flex-wrap gap-2">
                {deps.map((d, i) => (
                  <span key={i} className="rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">{d}</span>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">{icon}{title}</h2>
      {children}
    </section>
  );
}

function ConsultarIA({ estado }: { estado?: string }) {
  const [q, setQ] = useState('');
  const [ans, setAns] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true); setErr(null); setAns(null);
    try {
      const r = await preguntar(q.trim(), estado);
      setAns(cleanMd(r.respuesta || JSON.stringify(r)));
    } catch {
      setErr('El motor de consulta tardó o no está disponible. Intenta de nuevo en unos segundos.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-lg border border-primary/30 bg-primary/[0.03] p-4">
      <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
        <Sparkles size={16} className="text-primary" /> Pregúntale al radar (IA)
        {estado && <span className="text-xs font-normal text-muted-foreground">· {estado}</span>}
      </h2>
      <p className="mb-2 text-xs text-muted-foreground">
        Con lente de renta de maquinaria. Ej.: “¿dónde hay movimiento de tierras y a quién le hablo?”
      </p>
      <div className="flex gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && ask()}
          placeholder={estado ? `¿Dónde hay movimiento de tierras en ${estado}?` : '¿Dónde hay movimiento de tierras y a quién le hablo?'}
          className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary"
        />
        <button
          onClick={ask}
          disabled={busy}
          className="rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
        >
          {busy ? <Loader2 className="animate-spin" size={16} /> : 'Consultar'}
        </button>
      </div>
      {busy && <p className="mt-2 text-xs text-muted-foreground">El radar está analizando (puede tardar hasta ~1 min)…</p>}
      {err && <p className="mt-2 text-xs text-muted-foreground">{err}</p>}
      {ans && <p className="mt-3 whitespace-pre-wrap text-sm text-foreground leading-relaxed">{ans}</p>}
    </section>
  );
}
